/**
 * Clientes (CRM) — lista (redesign admin, Fase 4) sobre `CrmDataset.customers`.
 * A visão completa do cliente segue em `/clientes/:customerId` (CRM 360); esta
 * tela só localiza e navega. Sem a fonte, declara a pendência (nunca "0 clientes").
 */
import { createElement as h, useState, type ReactElement } from "react";
import { Link } from "react-router-dom";
import { CrmBoundary } from "../components/CrmBoundary.js";
import { StatusPanel } from "../components/StatusPanel.js";
import { AWAITING_CONTRACT_LABEL } from "../dashboard/kpis.js";
import { isCrmSourceAwaitingContract, type CrmDataset } from "../crm/dataset.js";
import { customerOrigins, filterCustomers } from "../crm/customer-list.js";
import { formatCount, formatDateTime } from "../format.js";
import { Avatar, Badge, Card, EmptyState, Field, PageHeader, controlClass } from "../ui/index.js";

export function ClientesView({ crm, initialQuery = "" }: { crm: CrmDataset; initialQuery?: string }): ReactElement {
  const [query, setQuery] = useState(initialQuery);
  const [origin, setOrigin] = useState<string | null>(null);
  const header = h(PageHeader, { title: "Clientes", description: "Cadastro unificado de clientes do CRM." });
  if (isCrmSourceAwaitingContract(crm, "customers")) {
    return h(
      "div",
      { className: "page page--clientes flex flex-col gap-6", "data-page": "clientes" },
      header,
      h(StatusPanel, { kind: "awaiting_source", title: `Cadastro de clientes ${AWAITING_CONTRACT_LABEL}` })
    );
  }
  const rows = filterCustomers(crm.customers, { query, origin });
  return h(
    "div",
    { className: "page page--clientes flex flex-col gap-6", "data-page": "clientes" },
    header,
    h(
      "div",
      { className: "flex flex-wrap items-end gap-3" },
      h(Field, {
        label: "Buscar (nome, e-mail ou telefone)",
        className: "min-w-[16rem] flex-1",
        children: (ids) =>
          h("input", {
            id: ids.id,
            type: "search",
            className: controlClass,
            value: query,
            "data-filter-control": "query",
            onChange: (e: { target: { value: string } }) => setQuery(e.target.value)
          })
      }),
      h(Field, {
        label: "Origem",
        children: (ids) =>
          h(
            "select",
            {
              id: ids.id,
              className: controlClass,
              value: origin ?? "",
              "data-filter-control": "origin",
              onChange: (e: { target: { value: string } }) => setOrigin(e.target.value || null)
            },
            h("option", { value: "" }, "Todas"),
            customerOrigins(crm.customers).map((o) => h("option", { key: o, value: o }, o))
          )
      })
    ),
    h(
      "p",
      { className: "text-sm text-muted", "data-customers-count": rows.length },
      `${formatCount(rows.length)} de ${formatCount(crm.customers.length)} cliente(s)`
    ),
    rows.length === 0
      ? h(EmptyState, { title: "Nenhum cliente encontrado" })
      : h(
          Card,
          null,
          h(
            "table",
            { className: "w-full text-left text-sm", "data-table": "clientes" },
            h(
              "thead",
              null,
              h(
                "tr",
                { className: "text-xs uppercase text-muted" },
                ["Cliente", "E-mail", "Telefone", "Origem", "Cadastro"].map((title) =>
                  h("th", { key: title, scope: "col", className: "px-4 py-3 font-bold" }, title)
                )
              )
            ),
            h(
              "tbody",
              null,
              rows.map((customer) =>
                h(
                  "tr",
                  { key: customer.id, "data-record-id": customer.id, className: "border-t border-border" },
                  h(
                    "td",
                    { className: "px-4 py-3" },
                    h(
                      "div",
                      { className: "flex items-center gap-3" },
                      h(Avatar, { name: customer.name }),
                      h(Link, { to: `/clientes/${customer.id}`, className: "font-extrabold text-ink hover:underline" }, customer.name)
                    )
                  ),
                  h("td", { className: "px-4 py-3" }, customer.email ?? "—"),
                  h("td", { className: "px-4 py-3 tabular-nums" }, customer.phone ?? "—"),
                  h("td", { className: "px-4 py-3" }, h(Badge, { tone: customer.origin === "whatsapp" ? "success" : "neutral" }, customer.origin)),
                  h("td", { className: "px-4 py-3 text-muted" }, formatDateTime(customer.createdAt))
                )
              )
            )
          )
        )
  );
}

export function Clientes(): ReactElement {
  return h(CrmBoundary, { children: (crm: CrmDataset) => h(ClientesView, { crm }) });
}
